<script>

import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css';
export default {
  name: 'TwWysiwyg',
  components: { QuillEditor },
  props: {
    modelValue: {
      type: [String,Number],
      default: null
    },
    label: {
      type: String,
      default: null
    },
    smallLabel: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    id: {
      type: String,
      default: () => Math.random().toString(36).substring(7)
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    limitedFormatting: {
      type: Boolean,
      default: false
    },
    mergeFields: {
      type: Array,
      default: () => []
    },
    options: {
      type: Object,
      default: () => ({
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link']
          ]
        },
        theme: 'snow',
        placeholder: ''
      })
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      quill: null,
    }
  },
  computed: {
    editorOptions() {
      if (!this.limitedFormatting) return this.options

      return {
        ...this.options,
        formats: ['bold', 'italic', 'link'],
        modules: {
          ...this.options.modules,
          toolbar: [['bold', 'italic', 'link']]
        }
      }
    },
    normalizedMergeFields() {
      return this.mergeFields
        .filter(field => field && field.value && field.label)
        .map(field => ({
          group: field.group || 'Merge Fields',
          description: field.description || field.value,
          ...field,
        }))
    },
    mergeFieldGroups() {
      return this.normalizedMergeFields.reduce((groups, field) => {
        const group = groups.find(group => group.label === field.group)
        if(group) {
          group.fields.push(field)
        }
        else {
          groups.push({
            label: field.group,
            fields: [field],
          })
        }
        return groups
      }, [])
    }
  },
  methods: {
    getSelection() {
      return this.quill?.getSelection(true) || { index: Math.max((this.quill?.getLength() || 1) - 1, 0), length: 0 }
    },
    insertLink(text, url, selection = this.getSelection()) {
      if (this.disabled || !this.quill || !text || !url) return false

      this.quill.updateContents([
        { retain: selection.index },
        { delete: selection.length },
        { insert: text, attributes: { link: url } }
      ], 'user')
      this.quill.setSelection(selection.index + text.length, 0, 'silent')
      this.quill.format('link', false, 'silent')
      return true
    },
    insertMergeField(field) {
      if(this.disabled || !this.quill || !field?.value) {
        return
      }

      const token = `{{ ${field.value} }}`
      const range = this.quill.getSelection(true)
      const index = range ? range.index : Math.max(this.quill.getLength() - 1, 0)

      this.quill.insertText(index, token, 'user')
      this.quill.setSelection(index + token.length, 0, 'user')
    },
  }
}
</script>

<template>
  <div class="w-full">
    <label v-if="label || $slots.label" :for="id" class="form-label"> <slot name="label">{{ label }}</slot> </label>
    <div v-if="smallLabel || $slots.smallLabel" class="form-label-sm">
      <slot name="smallLabel">
        {{ smallLabel }}
      </slot>
    </div>
    <div v-if="mergeFieldGroups.length || $slots.toolbar" class="merge-fields-toolbar">
      <div v-for="group in mergeFieldGroups" :key="group.label" class="merge-fields-group">
        <span class="merge-fields-label">{{ group.label }}</span>
        <button
          v-for="field in group.fields"
          :key="field.value"
          type="button"
          class="merge-field-button"
          :title="`${field.description} (${field.value})`"
          :disabled="disabled || !quill"
          @mousedown.prevent="insertMergeField(field)"
        >
          {{ field.label }}
        </button>
      </div>
      <slot name="toolbar" :disabled="disabled || !quill" />
    </div>
    <QuillEditor :id="id" :options="editorOptions" class="bg-white" content-type="html" :name="id" :readonly="disabled" :autocomplete="id" :content="modelValue" :required="required" :placeholder="placeholder" @ready="quill = $event" @update:content="$emit('update:modelValue', $event)" />
    <slot name="under" class="text-sm text-gray-500 mt-1  font-forms" />
  </div>
</template>

<style scoped>
.merge-fields-toolbar {
  @apply mb-2 flex flex-col gap-2 rounded border border-gray-200 bg-gray-50 p-2;
}

.merge-fields-group {
  @apply flex flex-wrap items-center gap-2;
}

.merge-fields-label {
  @apply mr-1 text-xs font-semibold uppercase tracking-wide text-gray-500;
}

.merge-field-button {
  @apply rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm hover:border-gray-400 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50;
}
</style>
